import React, { useState, useEffect, useRef, useCallback } from "react";
import { COLORS } from "../../theme/colors";
import ArtifactChips from "../skills/ArtifactChips";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { Badge, Label, RichTable } from "../../components/ui";
import PageHeader from "../../components/PageHeader";
import { MarkdownText } from "../../components/MarkdownText";
import { VerificationBadge } from "../../components/VerificationBadge";
import { ChatCitations } from "../../components/ChatCitations";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useAuth } from "../../contexts/AuthContext";
import { authApi, chatApi, agentApi, voiceApi, getCsrfToken } from "../../api/client";
import { createLogger } from "../../utils/logger";
import { useWebSocketChat } from "../../hooks/useWebSocketChat";
import { modelSettingOf, modelShortLabel } from "../../utils/agentModel";

const log = createLogger("ChannelsView");

const fmtVer = v => v ? `v${v.replace(/^v+/, "")}` : "";

const COL = { name: { flex: "2 1 0", minWidth: 100 }, model: { flex: "1.2 1 0", minWidth: 70 }, default: { width: 70, textAlign: "center", flexShrink: 0 }, priority: { width: 56, textAlign: "center", flexShrink: 0 }, intents: { flex: "1.5 1 0", minWidth: 80 }, status: { width: 68, textAlign: "center", flexShrink: 0 } };

const AgentsOnChannel = ({ agents, channel, label, mobile }) => {
  const onChannel = agents.filter(a => (a.channels || []).includes(channel));
  const statusDot = a => ({ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: a.status === "live" ? COLORS.green : a.status === "paused" ? COLORS.orange : COLORS.textDim });
  const statusBadge = a => ({ padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: a.status === "live" ? `${COLORS.green}15` : a.status === "paused" ? `${COLORS.orange}15` : `${COLORS.textDim}15`, color: a.status === "live" ? COLORS.green : a.status === "paused" ? COLORS.orange : COLORS.textDim });
  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: mobile ? "16px" : "20px 24px", marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>Agents on {label} ({onChannel.length})</div>
      {onChannel.length === 0 ? (
        <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "12px 0" }}>
          No agents assigned to {label}. Open an agent in the Builder, enable the {label} channel in the Trigger node, and save.
        </div>
      ) : mobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {onChannel.map(a => (
            <div key={a.id} style={{ padding: "12px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                  <div style={statusDot(a)} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}{a.version ? ` ${fmtVer(a.version)}` : ""}</span>
                </div>
                <span style={statusBadge(a)}>{a.status}</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", fontSize: 11, color: COLORS.textMuted }}>
                <span>{modelShortLabel(modelSettingOf(a))}</span>
                <span>P{a.priority ?? 100}</span>
                {a.is_default && <span style={{ padding: "1px 6px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: `${COLORS.accent}18`, color: COLORS.accentText, border: `1px solid ${COLORS.accent}30` }}>DEFAULT</span>}
              </div>
              {(a.routing_intents?.length > 0) && (
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                  {a.routing_intents.map((intent, i) => (
                    <span key={i} style={{ padding: "2px 7px", borderRadius: 8, fontSize: 9, fontWeight: 500, background: `${COLORS.pink}12`, color: COLORS.pink, border: `1px solid ${COLORS.pink}25` }}>{intent}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ borderRadius: 10, border: `1px solid ${COLORS.border}`, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}` }}>
            <div style={{ ...COL.name, fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>Agent</div>
            <div style={{ ...COL.model, fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>Model</div>
            <div style={{ ...COL.default, fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>Default</div>
            <div style={{ ...COL.priority, fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>Priority</div>
            <div style={{ ...COL.intents, fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>Intents</div>
            <div style={{ ...COL.status, fontSize: 10, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</div>
          </div>
          {onChannel.map((a, idx) => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: idx < onChannel.length - 1 ? `1px solid ${COLORS.border}` : "none", background: idx % 2 === 0 ? "transparent" : `${COLORS.bg}66` }}>
              <div style={{ ...COL.name, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <div style={statusDot(a)} />
                <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}{a.version ? ` ${fmtVer(a.version)}` : ""}</span>
              </div>
              <div style={{ ...COL.model, fontSize: 11, color: COLORS.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{modelShortLabel(modelSettingOf(a))}</div>
              <div style={{ ...COL.default, display: "flex", justifyContent: "center" }}>
                {a.is_default ? <span style={{ padding: "2px 7px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: `${COLORS.accent}18`, color: COLORS.accentText, border: `1px solid ${COLORS.accent}30` }}>YES</span> : <span style={{ fontSize: 11, color: COLORS.textDim }}>{"—"}</span>}
              </div>
              <div style={{ ...COL.priority, fontSize: 11, color: COLORS.textMuted, fontVariantNumeric: "tabular-nums" }}>{a.priority ?? 100}</div>
              <div style={{ ...COL.intents, display: "flex", gap: 4, flexWrap: "wrap", minWidth: 0 }}>
                {(a.routing_intents?.length > 0) ? a.routing_intents.map((intent, i) => (
                  <span key={i} style={{ padding: "2px 7px", borderRadius: 8, fontSize: 9, fontWeight: 500, background: `${COLORS.pink}12`, color: COLORS.pink, border: `1px solid ${COLORS.pink}25`, whiteSpace: "nowrap" }}>{intent}</span>
                )) : <span style={{ fontSize: 11, color: COLORS.textDim }}>{"—"}</span>}
              </div>
              <div style={{ ...COL.status, display: "flex", justifyContent: "center" }}>
                <span style={statusBadge(a)}>{a.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ChannelsView = ({ currentUser }) => {
  const bp = useBreakpoint();
  const { currentTenant } = useAuth();
  const [activeChannel, setActiveChannel] = useState("whatsapp");
  const [waConfig, setWaConfig] = useState(null);
  const [waForm, setWaForm] = useState({
    whatsapp_provider: "meta",
    phone_number_id: "", access_token: "", verify_token: "", app_secret: "",
    wasender_api_token: "", wasender_webhook_secret: "", wasender_phone_number: "",
    webhook_base_url: ""
  });
  const [waEditing, setWaEditing] = useState(false);
  const [waSaving, setWaSaving] = useState(false);
  const [waSaveMsg, setWaSaveMsg] = useState(null);
  // 2026-05-10 — Send Test Message card (origin/main 8528c96),
  // surgically merged with VI2 styling preserved.
  const [sendTestTo, setSendTestTo] = useState("");
  const [sendTestLoading, setSendTestLoading] = useState(false);
  const [sendTestResult, setSendTestResult] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [copied, setCopied] = useState("");
  const [showGuide, setShowGuide] = useState(false);

  // All agents for channel views
  const [channelAgents, setChannelAgents] = useState([]);

  // Helper: persist messages to localStorage
  const loadPersistedMessages = (key) => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : []; } catch { return []; }
  };
  const persistMessages = (key, msgs) => {
    try { localStorage.setItem(key, JSON.stringify(msgs.slice(-100))); } catch {} // keep last 100
  };

  // Web Chat test client state — real-time WebSocket streaming
  const {
    messages: wsMessages,
    connected: wsConnected,
    streaming: wsStreaming,
    agentTyping: wsAgentTyping,
    sendMessage: wsSendMessage,
    sendMessageWithFiles: wsSendMessageWithFiles,
    clearSession: wsClearSession,
  } = useWebSocketChat();
  const [chatInput, setChatInput] = useState("");
  const [chatAgents, setChatAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const chatEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const userScrolledUp = useRef(false);

  // File attachment state
  // Shape: { id, file_id, filename, size, content_type, status: 'uploading'|'processing'|'complete'|'error', progress: 0-100, error, parsed, parseStatus, textLength }
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);
  const fileUploading = pendingFiles.some(f => f.status === 'uploading' || f.status === 'processing');

  // Voice state
  const [voiceConfig, setVoiceConfig] = useState(null);
  const [voiceDeepgramKey, setVoiceDeepgramKey] = useState("");
  const [voiceElevenlabsKey, setVoiceElevenlabsKey] = useState("");
  const [voiceVoiceId, setVoiceVoiceId] = useState("21m00Tcm4TlvDq8ikWAM");
  const [voiceModel, setVoiceModel] = useState("eleven_turbo_v2_5");
  const [voiceStability, setVoiceStability] = useState(0.5);
  const [voiceSimilarity, setVoiceSimilarity] = useState(0.75);
  const [voiceStyle, setVoiceStyle] = useState(0.0);
  const [voiceSpeakerBoost, setVoiceSpeakerBoost] = useState(true);
  const [voiceSpeed, setVoiceSpeed] = useState(1.0);
  const [voiceEditing, setVoiceEditing] = useState(false);
  const [voiceSaving, setVoiceSaving] = useState(false);
  const [voiceSaved, setVoiceSaved] = useState(false);
  const [voiceTesting, setVoiceTesting] = useState(false);
  const [voiceTestResult, setVoiceTestResult] = useState(null);
  const [voiceAgents, setVoiceAgents] = useState([]);
  const [voiceSelectedAgent, setVoiceSelectedAgent] = useState("");
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState("disconnected");
  const [voiceTranscript, setVoiceTranscript] = useState([]);
  const livekitRoomRef = useRef(null);
  const [voiceRoomName, setVoiceRoomName] = useState("");
  const voiceRoomNameRef = useRef("");

  // SMS state
  const [smsConfig, setSmsConfig] = useState(null);
  const [smsForm, setSmsForm] = useState({ account_sid: "", auth_token: "", phone_number: "" });
  const [smsEditing, setSmsEditing] = useState(false);
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsSaveMsg, setSmsSaveMsg] = useState(null);
  const [smsTestResult, setSmsTestResult] = useState(null);
  const [smsTestLoading, setSmsTestLoading] = useState(false);

  // WhatsApp test client state
  const [waTestMessages, setWaTestMessages] = useState(() => loadPersistedMessages("i2_wa_messages"));
  const [waTestInput, setWaTestInput] = useState("");
  const [waTestLoading, setWaTestLoading] = useState(false);
  const [waTestAgent, setWaTestAgent] = useState("");

  // SMS test client state
  const [smsTestMessages, setSmsTestMessages] = useState(() => loadPersistedMessages("i2_sms_messages"));
  const [smsTestInput, setSmsTestInput] = useState("");
  const [smsTestMsgLoading, setSmsTestMsgLoading] = useState(false);
  const [smsTestAgent, setSmsTestAgent] = useState("");

  // Persist messages to localStorage whenever they change
  useEffect(() => { persistMessages("i2_wa_messages", waTestMessages); }, [waTestMessages]);
  useEffect(() => { persistMessages("i2_sms_messages", smsTestMessages); }, [smsTestMessages]);

  // Fetch all agents once for channel views
  useEffect(() => {
    agentApi.list().then(res => {
      if (res.success) {
        const all = res.data || [];
        setChannelAgents(all);
        setChatAgents(all);
        setVoiceAgents(all);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeChannel === "voice") {
      voiceApi.getConfig().then(res => {
        if (res.success) {
          setVoiceConfig(res.data);
          if (res.data.elevenlabs_voice_id) setVoiceVoiceId(res.data.elevenlabs_voice_id);
          if (res.data.elevenlabs_model) setVoiceModel(res.data.elevenlabs_model);
          if (res.data.elevenlabs_stability != null) setVoiceStability(res.data.elevenlabs_stability);
          if (res.data.elevenlabs_similarity_boost != null) setVoiceSimilarity(res.data.elevenlabs_similarity_boost);
          if (res.data.elevenlabs_style != null) setVoiceStyle(res.data.elevenlabs_style);
          if (res.data.elevenlabs_speaker_boost != null) setVoiceSpeakerBoost(res.data.elevenlabs_speaker_boost);
          if (res.data.elevenlabs_speed != null) setVoiceSpeed(res.data.elevenlabs_speed);
        }
      }).catch(() => {});
    }
  }, [activeChannel]);

  useEffect(() => {
    if (!userScrolledUp.current) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [wsMessages, wsAgentTyping]);

  // Keep room name ref in sync for cleanup
  useEffect(() => { voiceRoomNameRef.current = voiceRoomName; }, [voiceRoomName]);

  // Voice cleanup on unmount — notify backend so sessions don't linger
  useEffect(() => {
    const cleanup = () => {
      if (livekitRoomRef.current) {
        livekitRoomRef.current.removeAllListeners();
        livekitRoomRef.current.disconnect().catch(() => {});
        livekitRoomRef.current = null;
      }
      if (voiceRoomNameRef.current) {
        voiceApi.leaveSession(voiceRoomNameRef.current).catch(() => {});
        voiceRoomNameRef.current = "";
      }
    };
    window.addEventListener("beforeunload", cleanup);
    return () => {
      window.removeEventListener("beforeunload", cleanup);
      cleanup();
    };
  }, []);

  const handleVoiceSave = async () => {
    setVoiceSaving(true);
    try {
      const update = {
        elevenlabs_voice_id: voiceVoiceId,
        elevenlabs_model: voiceModel,
        elevenlabs_stability: voiceStability,
        elevenlabs_similarity_boost: voiceSimilarity,
        elevenlabs_style: voiceStyle,
        elevenlabs_speaker_boost: voiceSpeakerBoost,
        elevenlabs_speed: voiceSpeed,
      };
      if (voiceDeepgramKey) update.deepgram_api_key = voiceDeepgramKey;
      if (voiceElevenlabsKey) update.elevenlabs_api_key = voiceElevenlabsKey;
      await voiceApi.updateConfig(update);
      setVoiceDeepgramKey("");
      setVoiceElevenlabsKey("");
      setVoiceSaved(true);
      setTimeout(() => setVoiceSaved(false), 2000);
      const res = await voiceApi.getConfig();
      if (res.success) setVoiceConfig(res.data);
      setVoiceEditing(false);
    } catch {} finally { setVoiceSaving(false); }
  };

  const handleVoiceTest = async () => {
    setVoiceTesting(true);
    setVoiceTestResult(null);
    try {
      const res = await voiceApi.testConfig();
      setVoiceTestResult(res);
    } catch (err) {
      setVoiceTestResult({ all_ok: false, error: err.message });
    } finally { setVoiceTesting(false); }
  };

  const handleVoiceToggle = async () => {
    if (voiceActive) {
      // Disconnect and remove all listeners to prevent duplicates on reconnect
      if (livekitRoomRef.current) {
        livekitRoomRef.current.removeAllListeners();
        await livekitRoomRef.current.disconnect();
        livekitRoomRef.current = null;
      }
      // Notify backend for audit log
      if (voiceRoomName) {
        voiceApi.leaveSession(voiceRoomName).catch(() => {});
        setVoiceRoomName("");
      }
      setVoiceActive(false);
      setVoiceStatus("disconnected");
      return;
    }

    // Connect — lazy-load livekit-client on first use
    setVoiceStatus("connecting");
    try {
      const { Room, RoomEvent, Track } = await import("livekit-client");

      // Call the join endpoint to get a LiveKit token
      const res = await voiceApi.joinSession(voiceSelectedAgent || null);
      if (!res.livekit_url) throw new Error(res.error || res.detail || "Failed to join voice session");

      const { livekit_url, token, room_name } = res;
      setVoiceRoomName(room_name);

      // Resolve the client-facing LiveKit URL
      // In Docker: livekit_url is ws://livekit:7880 (internal Docker hostname)
      // Route through nginx /livekit/ reverse proxy for TLS termination
      const wsProto = window.location.protocol === "https:" ? "wss" : "ws";
      const livekitUrl = livekit_url.includes("livekit:")
        ? `${wsProto}://${window.location.host}/livekit/`
        : livekit_url;

      // Create and connect to the LiveKit room
      const room = new Room();

      room.on(RoomEvent.Connected, () => {
        setVoiceStatus("connected");
        setVoiceActive(true);
      });

      room.on(RoomEvent.Disconnected, () => {
        setVoiceStatus("disconnected");
        setVoiceActive(false);
        livekitRoomRef.current = null;
      });

      // Handle remote audio tracks (agent's voice)
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach();
          document.body.appendChild(el);
          el.style.display = "none";
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        track.detach().forEach(el => el.remove());
      });

      // Capture transcription events from the agent
      room.on(RoomEvent.TranscriptionReceived, (segments, participant) => {
        for (const segment of segments) {
          if (!segment.text) continue;
          const isAgent = participant?.identity?.startsWith("agent-") || participant?.identity === "voice-agent";
          const segId = segment.id || `${participant?.identity}-${Date.now()}`;
          setVoiceTranscript(prev => {
            const idx = prev.findIndex(t => t.id === segId);
            const entry = { id: segId, role: isAgent ? "assistant" : "user", text: segment.text, final: segment.final };
            if (idx >= 0) {
              // Replace existing segment (partial → final update)
              const next = [...prev];
              next[idx] = entry;
              return next;
            }
            return [...prev, entry];
          });
        }
      });

      room.on(RoomEvent.ParticipantConnected, (participant) => {
        if (participant.identity?.startsWith("agent-") || participant.identity === "voice-agent") {
          setVoiceStatus("ready");
        }
      });

      // Connect to the room
      await room.connect(livekitUrl, token);

      // Enable local microphone
      await room.localParticipant.setMicrophoneEnabled(true);

      livekitRoomRef.current = room;
    } catch (err) {
      log.error("Voice connection error:", err);
      setVoiceStatus("error");
      setVoiceActive(false);
      livekitRoomRef.current = null;
    }
  };

  const handleChatSend = () => {
    const text = chatInput.trim();
    const completedFiles = pendingFiles.filter(f => f.status === 'complete' && f.file_id);
    if ((!text && !completedFiles.length) || wsStreaming || fileUploading || !wsConnected) return;
    userScrolledUp.current = false;
    setChatInput("");
    setPendingFiles([]);
    if (completedFiles.length > 0) {
      wsSendMessageWithFiles(text || "Please review the attached files.", completedFiles.map(f => f.file_id), selectedAgent || null);
    } else {
      wsSendMessage(text, selectedAgent || null);
    }
  };

  const handleWaTestSend = async () => {
    const text = waTestInput.trim();
    if (!text || waTestLoading) return;
    const userId = currentUser?.id || "test-user";
    setWaTestInput("");
    setWaTestMessages(prev => [...prev, { role: "user", text, ts: Date.now() }]);
    setWaTestLoading(true);
    try {
      const res = await chatApi.sendTestWhatsApp(userId, text, waTestAgent || null);
      if (res.success) {
        setWaTestMessages(prev => [...prev, {
          role: "assistant", text: res.data.reply,
          agentName: res.data.agent_name, intent: res.data.intent,
          latency: res.data.latency_ms, ts: Date.now(),
        }]);
      }
    } catch (err) {
      setWaTestMessages(prev => [...prev, { role: "error", text: err.message || "Failed to send", ts: Date.now() }]);
    } finally { setWaTestLoading(false); }
  };

  const handleSmsTestSend = async () => {
    const text = smsTestInput.trim();
    if (!text || smsTestMsgLoading) return;
    const userId = currentUser?.id || "test-user";
    setSmsTestInput("");
    setSmsTestMessages(prev => [...prev, { role: "user", text, ts: Date.now() }]);
    setSmsTestMsgLoading(true);
    try {
      const res = await chatApi.sendTestSMS(userId, text, smsTestAgent || null);
      if (res.success) {
        setSmsTestMessages(prev => [...prev, {
          role: "assistant", text: res.data.reply,
          agentName: res.data.agent_name, intent: res.data.intent,
          latency: res.data.latency_ms, ts: Date.now(),
        }]);
      }
    } catch (err) {
      setSmsTestMessages(prev => [...prev, { role: "error", text: err.message || "Failed to send", ts: Date.now() }]);
    } finally { setSmsTestMsgLoading(false); }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    for (const file of files) {
      const tempId = 'upload_' + Math.random().toString(36).slice(2, 10);

      setPendingFiles(prev => [...prev, {
        id: tempId, file_id: null, filename: file.name, size: file.size,
        content_type: file.type, status: 'uploading', progress: 0,
        error: null, parsed: null, parseStatus: null, textLength: null,
      }]);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('agent_id', selectedAgent || '');

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const pct = Math.round((event.loaded / event.total) * 100);
          setPendingFiles(prev => prev.map(f => f.id === tempId ? { ...f, progress: pct } : f));
        }
      };

      xhr.upload.onloadend = () => {
        setPendingFiles(prev => prev.map(f => f.id === tempId && f.status === 'uploading' ? { ...f, status: 'processing', progress: 100 } : f));
      };

      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300 && data.success) {
            setPendingFiles(prev => prev.map(f => f.id === tempId ? {
              ...f, file_id: data.data.file_id, status: 'complete', progress: 100,
              parsed: data.data.parsed ?? (data.data.text_length > 0),
              parseStatus: data.data.parse_status || `${data.data.text_length} chars`,
              textLength: data.data.text_length,
            } : f));
          } else {
            const errMsg = typeof data.detail === 'object' ? data.detail.message : (data.detail || `Upload failed (HTTP ${xhr.status})`);
            setPendingFiles(prev => prev.map(f => f.id === tempId ? { ...f, status: 'error', error: errMsg } : f));
          }
        } catch {
          setPendingFiles(prev => prev.map(f => f.id === tempId ? { ...f, status: 'error', error: 'Invalid server response' } : f));
        }
      };

      xhr.onerror = () => {
        setPendingFiles(prev => prev.map(f => f.id === tempId ? { ...f, status: 'error', error: 'Network error' } : f));
      };

      xhr.open('POST', '/api/files/upload');
      // F (#16): cookie auth (withCredentials) + double-submit CSRF header.
      xhr.withCredentials = true;
      const csrf = getCsrfToken();
      if (csrf) xhr.setRequestHeader('X-CSRF-Token', csrf);
      xhr.send(formData);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (idOrFileId) => {
    setPendingFiles(prev => prev.filter(f => f.id !== idOrFileId && f.file_id !== idOrFileId));
  };

  const handleChatClear = () => {
    wsClearSession();
    setPendingFiles([]);
  };

  const handleRichAction = (action, url) => {
    if (url) {
      window.open(url, "_blank", "noopener");
      return;
    }
    wsSendMessage(action, selectedAgent || null);
    setChatInput("");
  };

  const channels = [
    { id: "whatsapp", label: "WhatsApp", icon: "\u{1F4AC}", color: COLORS.green },
    { id: "chat", label: "Web Chat", icon: "\u{1F310}", color: COLORS.accentText },
    { id: "voice", label: "Voice", icon: "\u{1F4DE}", color: COLORS.orange },
    { id: "sms", label: "SMS", icon: "\u{1F4F1}", color: COLORS.pink },
    { id: "email", label: "Email", icon: "\u2709", color: COLORS.cyan },
  ];

  useEffect(() => {
    authApi.getWhatsAppConfig().then(res => {
      if (res.success) {
        setWaConfig(res.data);
        setWaForm({
          whatsapp_provider: res.data.provider || "meta",
          phone_number_id: res.data.phone_number_id || "",
          access_token: res.data.access_token || "",
          verify_token: res.data.verify_token || "",
          app_secret: res.data.app_secret || "",
          wasender_api_token: res.data.wasender_api_token || "",
          wasender_webhook_secret: res.data.wasender_webhook_secret || "",
          wasender_phone_number: res.data.wasender_phone_number || "",
          webhook_base_url: res.data.webhook_base_url || "",
        });
      }
    }).catch(() => {});
    authApi.getSMSConfig().then(res => {
      if (res.success) {
        setSmsConfig(res.data);
        setSmsForm({
          account_sid: res.data.account_sid || "",
          auth_token: res.data.auth_token || "",
          phone_number: res.data.phone_number || "",
        });
      }
    }).catch(() => {});
  }, []);

  const handleTest = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await authApi.testWhatsAppConfig();
      setTestResult({ success: true, data: res.data });
    } catch (err) {
      setTestResult({ success: false, error: err.message || "Connection failed" });
    } finally {
      setTestLoading(false);
    }
  };

  const copyToClipboard = (text, key) => {
    if (!text) return;
    // Try modern clipboard API first, fall back to execCommand
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
    setCopied(key || text);
    setTimeout(() => setCopied(""), 2000);
  };

  const fallbackCopy = (text) => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  };

  const copyWebhook = () => copyToClipboard(waConfig?.webhook_url);

  const handleWaSave = async () => {
    setWaSaving(true);
    setWaSaveMsg(null);
    try {
      const payload = {
        whatsapp_provider: waForm.whatsapp_provider,
        whatsapp_phone_number_id: waForm.phone_number_id,
        whatsapp_access_token: waForm.access_token,
        whatsapp_verify_token: waForm.verify_token,
        whatsapp_app_secret: waForm.app_secret,
        wasender_api_token: waForm.wasender_api_token,
        wasender_webhook_secret: waForm.wasender_webhook_secret,
        wasender_phone_number: waForm.wasender_phone_number,
        webhook_base_url: waForm.webhook_base_url,
      };
      const res = await authApi.updateWhatsAppConfig(payload);
      setWaSaveMsg({ success: true, text: "Configuration saved" });
      // Re-fetch to get updated masked values
      const fresh = await authApi.getWhatsAppConfig();
      if (fresh.success) {
        setWaConfig(fresh.data);
        setWaForm({
          whatsapp_provider: fresh.data.provider || "meta",
          phone_number_id: fresh.data.phone_number_id || "",
          access_token: fresh.data.access_token || "",
          verify_token: fresh.data.verify_token || "",
          app_secret: fresh.data.app_secret || "",
          wasender_api_token: fresh.data.wasender_api_token || "",
          wasender_webhook_secret: fresh.data.wasender_webhook_secret || "",
          wasender_phone_number: fresh.data.wasender_phone_number || "",
          webhook_base_url: fresh.data.webhook_base_url || "",
        });
      }
      setWaEditing(false);
    } catch (err) {
      setWaSaveMsg({ success: false, text: err.message || "Save failed" });
    } finally {
      setWaSaving(false);
      setTimeout(() => setWaSaveMsg(null), 3000);
    }
  };

  // Send Test Message handler — fires a real WhatsApp message via the
  // configured provider (Meta or WaSender). Independent from the
  // generic Test Connection above; this writes an actual message.
  const handleSendTest = async () => {
    if (!sendTestTo.trim()) return;
    setSendTestLoading(true);
    setSendTestResult(null);
    try {
      await authApi.sendTestWhatsAppMessage(sendTestTo.trim(), "This is a test message from I\u00B2 Agentic Platform.");
      setSendTestResult({ success: true, sent_to: sendTestTo.trim() });
    } catch (e) {
      setSendTestResult({ success: false, error: String(e?.message || e) });
    } finally {
      setSendTestLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8,
    border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textMuted,
    fontSize: 12, fontFamily: "monospace", outline: "none",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      <div style={{ padding: bp.isMobile ? "16px 12px 0" : "24px 28px 0", flexShrink: 0 }}>
        <PageHeader
          title="Channels"
          subtitle="WhatsApp, Voice, SMS, Web Chat, Email, Webhooks"
        />
      </div>
      <div style={{ display: "flex", flexDirection: bp.isMobile ? "column" : "row", flex: 1, overflow: "hidden" }}>
      {/* Channel list */}
      <div style={{
        width: bp.isMobile ? "100%" : 260, background: COLORS.surface,
        borderRight: bp.isMobile ? "none" : `1px solid ${COLORS.border}`,
        borderBottom: bp.isMobile ? `1px solid ${COLORS.border}` : "none",
        padding: bp.isMobile ? "12px 14px" : "20px 14px",
        ...(bp.isMobile ? { display: "flex", gap: 8, overflowX: "auto", flexShrink: 0 } : {}),
      }}>
        {!bp.isMobile && (
          <Label style={{ marginBottom: 16, letterSpacing: "0.08em" }}>Channels</Label>
        )}
        {channels.map(ch => {
          const agentsOnChannel = channelAgents.filter(a => (a.channels || []).includes(ch.id));
          const liveAgents = agentsOnChannel.filter(a => a.status === "live");
          const isConfigured = ch.id === "whatsapp" ? waConfig?.configured
            : ch.id === "voice" ? !!(voiceConfig?.deepgram_api_key)
            : ch.id === "sms" ? smsConfig?.configured
            : true;
          const hasAgents = agentsOnChannel.length > 0;
          return (
            // The channel switcher is a plain <div onClick> — no <button>,
            // no role, no test hook. So `button:has-text("Chat")` cannot
            // reach it, and a test trying to open the Chat channel silently
            // stayed on the default (whatsapp) and then bound to whatever
            // other input it found first. `data-channel` makes the target
            // addressable; `aria-selected` + role make it addressable to a
            // screen reader for the same reason.
            <div key={ch.id} onClick={() => setActiveChannel(ch.id)}
              data-testid="channel-tab"
              data-channel={ch.id}
              role="tab"
              aria-selected={activeChannel === ch.id}
              style={{
              padding: "14px 14px", borderRadius: 12, marginBottom: 6, cursor: "pointer",
              background: activeChannel === ch.id ? COLORS.surfaceHover : "transparent",
              border: `1px solid ${activeChannel === ch.id ? COLORS.border : "transparent"}`,
              transition: "all 0.15s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: `${ch.color}15`, border: `1px solid ${ch.color}30`,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
                }}>{ch.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{ch.label}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                    {hasAgents
                      ? `${liveAgents.length} live \u00B7 ${agentsOnChannel.length} total`
                      : isConfigured ? "No agents assigned" : "Not configured"
                    }
                  </div>
                </div>
                <div style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: hasAgents && liveAgents.length > 0 ? ch.color : COLORS.textDim,
                  boxShadow: hasAgents && liveAgents.length > 0 ? `0 0 6px ${ch.color}88` : "none",
                }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Channel config panel */}
      <div className="panel-scroll" style={{ flex: 1, padding: bp.isMobile ? "16px 14px 80px" : "24px 28px", overflowY: "auto" }}>

        {/* -- WhatsApp Config -- */}
        {activeChannel === "whatsapp" && (
          <div>
            <div style={{ display: "flex", alignItems: bp.isMobile ? "flex-start" : "center", justifyContent: "space-between", marginBottom: 24, flexDirection: bp.isMobile ? "column" : "row", gap: bp.isMobile ? 12 : 0 }}>
              <div>
                <div style={{ fontSize: bp.isMobile ? 18 : 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>WhatsApp Configuration</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Connect your WhatsApp Business account to receive messages</div>
              </div>
            </div>

            {/* Test result */}
            {testResult && (
              <div style={{
                padding: "12px 16px", borderRadius: 10, marginBottom: 20,
                background: testResult.success ? `${COLORS.green}10` : `${COLORS.pink}10`,
                border: `1px solid ${testResult.success ? COLORS.green : COLORS.pink}25`,
                color: testResult.success ? COLORS.green : COLORS.pink,
                fontSize: 12, fontWeight: 500,
              }}>
                {testResult.success
                  ? waForm.whatsapp_provider === "wasender"
                    ? `Connected \u2014 ${testResult.data?.verified_name || "WasenderAPI"}${testResult.data?.phone_number ? ` (${testResult.data.phone_number})` : ""}`
                    : `Connected \u2014 ${testResult.data?.verified_name || "WhatsApp Business"} (${testResult.data?.phone_number || ""})`
                  : `Connection failed: ${testResult.error}`
                }
              </div>
            )}

            {/* Status card */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px", marginBottom: 20,
              display: "flex", alignItems: "center", gap: 16,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: waConfig?.configured ? `${COLORS.green}12` : `${COLORS.orange}12`,
                border: `1px solid ${waConfig?.configured ? COLORS.green : COLORS.orange}25`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
              }}>{waConfig?.configured ? "\u2713" : "!"}</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
                  {waConfig?.configured ? "WhatsApp Connected" : "WhatsApp Not Configured"}
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                  {waConfig?.configured
                    ? (waConfig?.provider === "wasender"
                        ? `WasenderAPI${waConfig.wasender_phone_number ? ` · ${waConfig.wasender_phone_number}` : " · token configured"}`
                        : `Meta · Phone Number ID: ${waConfig.phone_number_id}`)
                    : "Set your WhatsApp credentials to start receiving messages"
                  }
                </div>
              </div>
            </div>

            <AgentsOnChannel agents={channelAgents} channel="whatsapp" label="WhatsApp" mobile={bp.isMobile} />

            {/* Configuration form */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px", marginBottom: 20,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Configuration</div>
                {!waEditing ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={handleTest} disabled={testLoading || !waConfig?.configured} style={{
                      padding: "6px 14px", borderRadius: 8, border: "none", cursor: testLoading ? "wait" : "pointer",
                      background: !waConfig?.configured ? COLORS.border : `linear-gradient(135deg, ${COLORS.green}, #059669)`,
                      color: !waConfig?.configured ? COLORS.textDim : "#fff",
                      fontSize: 12, fontWeight: 600,
                      boxShadow: !waConfig?.configured ? "none" : "0 4px 12px rgba(16,185,129,0.3)",
                    }}>{testLoading ? "Testing..." : "Test Connection"}</button>
                    <button onClick={() => setWaEditing(true)} style={{
                      padding: "6px 14px", borderRadius: 8, border: `1px solid ${COLORS.accent}40`,
                      background: `${COLORS.accent}10`, color: COLORS.accentText, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}>Edit</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => { setWaEditing(false); if (waConfig) setWaForm({ whatsapp_provider: waConfig.provider || "meta", phone_number_id: waConfig.phone_number_id || "", access_token: waConfig.access_token || "", verify_token: waConfig.verify_token || "", app_secret: waConfig.app_secret || "", wasender_api_token: waConfig.wasender_api_token || "", wasender_webhook_secret: waConfig.wasender_webhook_secret || "", wasender_phone_number: waConfig.wasender_phone_number || "", webhook_base_url: waConfig.webhook_base_url || "" }); }} style={{
                      padding: "6px 14px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                      background: COLORS.surface, color: COLORS.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}>Cancel</button>
                    <button onClick={handleWaSave} disabled={waSaving} style={{
                      padding: "6px 14px", borderRadius: 8, border: "none",
                      background: `linear-gradient(135deg, ${COLORS.green}, #059669)`, color: "#fff",
                      fontSize: 12, fontWeight: 600, cursor: waSaving ? "wait" : "pointer",
                      boxShadow: "0 4px 12px rgba(16,185,129,0.3)",
                    }}>{waSaving ? "Saving..." : "Save"}</button>
                  </div>
                )}
              </div>

              {waSaveMsg && (
                <div style={{
                  padding: "8px 12px", borderRadius: 8, marginBottom: 12,
                  background: waSaveMsg.success ? `${COLORS.green}10` : `${COLORS.pink}10`,
                  border: `1px solid ${waSaveMsg.success ? COLORS.green : COLORS.pink}25`,
                  color: waSaveMsg.success ? COLORS.green : COLORS.pink, fontSize: 12, fontWeight: 500,
                }}>{waSaveMsg.text}</div>
              )}

              {!waEditing && testResult && (
                <div style={{
                  padding: "10px 14px", borderRadius: 8, marginBottom: 14,
                  background: testResult.success ? `${COLORS.green}10` : `${COLORS.pink}10`,
                  border: `1px solid ${testResult.success ? COLORS.green : COLORS.pink}25`,
                  color: testResult.success ? COLORS.green : COLORS.pink, fontSize: 12, fontWeight: 500,
                }}>
                  {testResult.success
                    ? `Connected \u2014 ${testResult.data.verified_name || "WhatsApp Business"} (${testResult.data.phone_number})`
                    : `Connection failed: ${testResult.error}`
                  }
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {/* Provider selector */}
                <div>
                  <Label style={{ marginBottom: 8 }}>Provider</Label>
                  <div style={{ display: "inline-flex", background: COLORS.bg, borderRadius: RADIUS.inner, border: `1px solid ${COLORS.border}`, padding: 3 }}>
                    {[{ value: "meta", label: "Meta Cloud API" }, { value: "wasender", label: "WasenderAPI" }].map(p => (
                      <button key={p.value}
                        onClick={() => waEditing && setWaForm(f => ({ ...f, whatsapp_provider: p.value }))}
                        disabled={!waEditing}
                        style={{
                          padding: "6px 16px", borderRadius: RADIUS.deep, border: "none", fontSize: 12, fontWeight: 600,
                          cursor: waEditing ? "pointer" : "default", transition: "all 0.15s",
                          background: waForm.whatsapp_provider === p.value ? COLORS.accent : "transparent",
                          color: waForm.whatsapp_provider === p.value ? "#fff" : COLORS.textMuted,
                          opacity: waEditing ? 1 : 0.85,
                        }}
                      >{p.label}</button>
                    ))}
                  </div>
                </div>

                {/* Meta Cloud API fields */}
                {waForm.whatsapp_provider !== "wasender" && [
                  { key: "phone_number_id", label: "Phone Number ID", placeholder: "e.g. 109876543210", type: "text" },
                  { key: "access_token", label: "Access Token", placeholder: "Enter access token", type: "password" },
                  { key: "verify_token", label: "Verify Token", placeholder: "Your custom verify token", type: "text" },
                  { key: "app_secret", label: "App Secret", placeholder: "Enter app secret", type: "password" },
                ].map(field => (
                  <div key={field.key}>
                    <Label style={{ marginBottom: 6 }}>{field.label}</Label>
                    <input
                      type={waEditing ? field.type : "text"}
                      readOnly={!waEditing}
                      value={waForm[field.key]}
                      onChange={e => setWaForm(f => ({ ...f, [field.key]: e.target.value }))}
                      onFocus={e => { if (waEditing && (field.type === "password") && e.target.value.startsWith("***")) setWaForm(f => ({ ...f, [field.key]: "" })); }}
                      placeholder={waEditing ? field.placeholder : ""}
                      style={{
                        ...inputStyle, width: "100%", boxSizing: "border-box",
                        background: waEditing ? COLORS.bg : COLORS.surface,
                        cursor: waEditing ? "text" : "default",
                        color: !waForm[field.key] || waForm[field.key].startsWith("***") ? COLORS.textDim : COLORS.text,
                      }}
                    />
                  </div>
                ))}

                {/* WasenderAPI fields */}
                {waForm.whatsapp_provider === "wasender" && [
                  { key: "wasender_phone_number", label: "Phone Number", placeholder: "e.g. +1234567890", type: "text" },
                  { key: "wasender_api_token", label: "API Token", placeholder: "Enter your WasenderAPI token", type: "password" },
                  { key: "wasender_webhook_secret", label: "Webhook Secret", placeholder: "Enter webhook secret (optional)", type: "password" },
                ].map(field => (
                  <div key={field.key}>
                    <Label style={{ marginBottom: 6 }}>{field.label}</Label>
                    <input
                      type={waEditing ? field.type : "text"}
                      readOnly={!waEditing}
                      value={waForm[field.key]}
                      onChange={e => setWaForm(f => ({ ...f, [field.key]: e.target.value }))}
                      onFocus={e => { if (waEditing && (field.type === "password") && e.target.value.startsWith("***")) setWaForm(f => ({ ...f, [field.key]: "" })); }}
                      placeholder={waEditing ? field.placeholder : ""}
                      style={{
                        ...inputStyle, width: "100%", boxSizing: "border-box",
                        background: waEditing ? COLORS.bg : COLORS.surface,
                        cursor: waEditing ? "text" : "default",
                        color: !waForm[field.key] || waForm[field.key].startsWith("***") ? COLORS.textDim : COLORS.text,
                      }}
                    />
                  </div>
                ))}

                {/* Common: Webhook Base URL */}
                <div>
                  <Label style={{ marginBottom: 6 }}>Webhook Base URL</Label>
                  <input
                    type="text"
                    readOnly={!waEditing}
                    value={waForm.webhook_base_url}
                    onChange={e => setWaForm(f => ({ ...f, webhook_base_url: e.target.value }))}
                    placeholder={waEditing ? "https://your-domain.com" : ""}
                    style={{
                      ...inputStyle, width: "100%", boxSizing: "border-box",
                      background: waEditing ? COLORS.bg : COLORS.surface,
                      cursor: waEditing ? "text" : "default",
                      color: !waForm.webhook_base_url ? COLORS.textDim : COLORS.text,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Webhook URL */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px", marginBottom: 20,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Webhook URL</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>
                {waForm.whatsapp_provider === "wasender"
                  ? "Enter this URL in your WasenderAPI webhook settings"
                  : "Enter this URL in your Meta WhatsApp webhook configuration"
                }
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input type="text" readOnly value={waConfig?.webhook_url || ""} style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => copyToClipboard(waConfig?.webhook_url, "wa-webhook")} style={{
                  padding: "10px 16px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                  background: copied === "wa-webhook" ? `${COLORS.green}15` : COLORS.bg,
                  color: copied === "wa-webhook" ? COLORS.green : COLORS.textMuted,
                  fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                }}>{copied === "wa-webhook" ? "Copied!" : "Copy"}</button>
              </div>
            </div>

            {/* Send Test Message — fires a real WhatsApp message via the
                configured provider. Independent from generic Test Connection
                above (which tests credentials only). VI2 tokens preserved. */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px", marginBottom: 20,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Send Test Message</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>
                Send a real WhatsApp message to verify your configuration is working correctly.
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <input
                  type="text"
                  value={sendTestTo}
                  onChange={e => setSendTestTo(e.target.value)}
                  placeholder="e.g. +1234567890"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  onClick={handleSendTest}
                  disabled={sendTestLoading || !sendTestTo.trim()}
                  style={{
                    padding: "10px 18px", borderRadius: RADIUS.deep,
                    border: `1px solid ${COLORS.border}`,
                    background: sendTestLoading || !sendTestTo.trim() ? COLORS.surface : COLORS.accent,
                    color: sendTestLoading || !sendTestTo.trim() ? COLORS.textMuted : "#fff",
                    fontSize: 12, fontWeight: 600,
                    cursor: sendTestLoading || !sendTestTo.trim() ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {sendTestLoading ? "Sending\u2026" : "Send"}
                </button>
              </div>
              {sendTestResult && (
                <div style={{
                  fontSize: 12, padding: "8px 12px", borderRadius: RADIUS.deep,
                  background: sendTestResult.success ? `${COLORS.green}18` : `${(COLORS.red || "#ef4444")}18`,
                  color: sendTestResult.success ? COLORS.green : (COLORS.red || "#ef4444"),
                  border: `1px solid ${sendTestResult.success ? COLORS.green : (COLORS.red || "#ef4444")}40`,
                }}>
                  {sendTestResult.success
                    ? `Message sent to ${sendTestResult.sent_to || sendTestTo}`
                    : `Error: ${sendTestResult.error || "Failed to send message"}`}
                </div>
              )}
            </div>

            {/* Setup Guide */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px",
            }}>
              <div onClick={() => setShowGuide(!showGuide)} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Setup Guide</div>
                <span style={{ fontSize: 12, color: COLORS.textMuted, transition: "transform 0.2s", transform: showGuide ? "rotate(180deg)" : "none" }}>{"\u25BC"}</span>
              </div>
              {showGuide && (
                <div style={{ marginTop: 16 }}>
                  {waForm.whatsapp_provider === "wasender" ? (
                    /* WaSender setup steps */
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {[
                        { step: 1, title: "Sign up at wasenderapi.com", desc: "Create an account at wasenderapi.com and set up your WhatsApp instance." },
                        { step: 2, title: "Get your API Token", desc: "From your WasenderAPI dashboard, copy the API Token for your instance." },
                        { step: 3, title: "Enter your credentials", desc: "Click Edit above and paste your API Token. Optionally add the Webhook Secret for signature verification." },
                        { step: 4, title: "Configure the webhook", desc: "In your WasenderAPI instance settings, set the webhook URL to the one shown above. Incoming messages will be forwarded to your platform." },
                        { step: 5, title: "Test the connection", desc: "Click 'Test Connection' above to verify your API Token is valid." },
                      ].map(s => (
                        <div key={s.step} style={{ display: "flex", gap: 12 }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                            background: `${COLORS.accent}15`, border: `1px solid ${COLORS.accent}30`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, color: COLORS.accentText,
                          }}>{s.step}</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{s.title}</div>
                            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2, lineHeight: 1.5 }}>{s.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Meta Cloud API setup steps */
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {[
                        { step: 1, title: "Create a Meta App", desc: "Go to developers.facebook.com, create an app, and add the WhatsApp product." },
                        { step: 2, title: "Get your credentials", desc: "From your WhatsApp app dashboard, copy the Phone Number ID, Access Token, and App Secret." },
                        { step: 3, title: "Enter your credentials", desc: "Click Edit above and enter your Phone Number ID, Access Token, Verify Token, and App Secret. Or add them directly to your backend .env file:" },
                        { step: 4, title: "Configure the webhook", desc: "In Meta's WhatsApp Configuration, set the webhook URL to the one shown above and use your Verify Token." },
                        { step: 5, title: "Test the connection", desc: "Click 'Test Connection' above to verify everything works. Changes take effect immediately \u2014 no restart needed." },
                      ].map(s => (
                        <div key={s.step} style={{ display: "flex", gap: 12 }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                            background: `${COLORS.accent}15`, border: `1px solid ${COLORS.accent}30`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, color: COLORS.accentText,
                          }}>{s.step}</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{s.title}</div>
                            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2, lineHeight: 1.5 }}>{s.desc}</div>
                            {s.step === 3 && (
                              <div style={{
                                marginTop: 8, padding: "10px 12px", borderRadius: RADIUS.deep,
                                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                                fontSize: 11, fontFamily: "monospace", color: COLORS.textMuted, lineHeight: 1.8,
                              }}>
                                WHATSAPP_PHONE_NUMBER_ID=your_id<br/>
                                WHATSAPP_ACCESS_TOKEN=your_token<br/>
                                WHATSAPP_VERIFY_TOKEN=your_verify_token<br/>
                                WHATSAPP_APP_SECRET=your_secret
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* WhatsApp Test Client */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              overflow: "hidden", marginTop: 20,
            }}>
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Test WhatsApp Client</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {(() => {
                    const onWa = channelAgents.filter(a => (a.channels || []).includes("whatsapp"));
                    const liveOnWa = onWa.filter(a => a.status === "live");
                    return onWa.length > 0 && (
                      <select value={waTestAgent} onChange={e => setWaTestAgent(e.target.value)}
                        style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 11, outline: "none", cursor: "pointer" }}>
                        <option value="">Auto-route ({liveOnWa.length} live)</option>
                        {onWa.map(a => <option key={a.id} value={a.id}>{a.name} ({a.status})</option>)}
                      </select>
                    );
                  })()}
                  <button onClick={() => { setWaTestMessages([]); localStorage.removeItem("i2_wa_messages"); }} style={{
                    padding: "5px 12px", borderRadius: 6, border: `1px solid ${COLORS.border}`,
                    background: COLORS.surface, color: COLORS.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer",
                  }}>Clear</button>
                </div>
              </div>
              <div style={{ height: 380, display: "flex", flexDirection: "column" }}>
                <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                  {waTestMessages.length === 0 && (
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: COLORS.textDim, gap: 8 }}>
                      <div style={{ fontSize: 32 }}>{"\uD83D\uDCF1"}</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>Test your WhatsApp agent</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim }}>Messages are processed through the orchestrator with channel=whatsapp</div>
                    </div>
                  )}
                  {waTestMessages.map((msg, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", maxWidth: "100%" }}>
                      <div style={{
                        maxWidth: "80%", padding: "10px 14px", borderRadius: 12,
                        background: msg.role === "user" ? COLORS.green : msg.role === "error" ? `${COLORS.pink}18` : COLORS.bg,
                        border: msg.role === "error" ? `1px solid ${COLORS.pink}30` : msg.role === "assistant" ? `1px solid ${COLORS.border}` : "none",
                        color: msg.role === "user" ? "#fff" : msg.role === "error" ? COLORS.pink : COLORS.text,
                      }}>
                        {msg.role === "assistant" && msg.agentName && (
                          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.green, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                            {msg.agentName}
                            {msg.latency && <span style={{ color: COLORS.textDim, fontWeight: 500 }}>{msg.latency}ms</span>}
                          </div>
                        )}
                        <MarkdownText text={msg.text} style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }} />
                      </div>
                    </div>
                  ))}
                  {waTestLoading && (
                    <div style={{ display: "flex", justifyContent: "flex-start" }}>
                      <div style={{ padding: "10px 14px", borderRadius: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`, fontSize: 13, color: COLORS.textDim }}>Typing...</div>
                    </div>
                  )}
                </div>
                <div style={{ padding: "12px 20px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 8 }}>
                  <input type="text" value={waTestInput} onChange={e => setWaTestInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleWaTestSend(); } }}
                    placeholder="Type a WhatsApp message..."
                    style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 13, outline: "none" }} />
                  <button onClick={handleWaTestSend} disabled={waTestLoading || !waTestInput.trim()}
                    style={{
                      padding: "10px 20px", borderRadius: 10, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer",
                      background: waTestLoading || !waTestInput.trim() ? COLORS.border : `linear-gradient(135deg, ${COLORS.green}, #059669)`,
                      color: waTestLoading || !waTestInput.trim() ? COLORS.textDim : "#fff",
                    }}>Send</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* -- Web Chat -- */}
        {activeChannel === "chat" && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: bp.isMobile ? 18 : 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>Web Chat</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Built-in chat channel for your web application</div>
            </div>

            {/* Status card */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px", display: "flex", alignItems: "center", gap: 16, marginBottom: 16,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: `${COLORS.accent}12`, border: `1px solid ${COLORS.accent}25`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
              }}>{"\u2713"}</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Web Chat Active</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                  The web chat channel is built into the platform. Users can chat via the Chat tab or WebSocket connections.
                </div>
              </div>
            </div>

            <AgentsOnChannel agents={channelAgents} channel="chat" label="Web Chat" mobile={bp.isMobile} />

            {/* Endpoints */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px", marginBottom: 16,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>Endpoints</div>
              {[
                { label: "REST API", value: "/api/chat/message" },
                { label: "WebSocket", value: "/ws/chat?token=JWT" },
              ].map(ep => (
                <div key={ep.label} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`,
                }}>
                  <span style={{ fontSize: 12, color: COLORS.textMuted }}>{ep.label}</span>
                  <code style={{ fontSize: 11, color: COLORS.accentText, background: `${COLORS.accent}10`, padding: "3px 8px", borderRadius: 5 }}>{ep.value}</code>
                </div>
              ))}
            </div>

            {/* Chat Test Client
                `data-ws-state` is the machine-readable readiness signal. The
                human-readable "Connected — real-time streaming enabled" text
                below only renders while `wsMessages.length === 0`, so it
                vanishes the moment a conversation starts and cannot be waited
                on reliably. This attribute is always present and always
                current, which is what an automated check needs. */}
            <div
              data-testid="chat-test-client"
              data-ws-state={wsConnected ? "connected" : "connecting"}
              style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              overflow: "hidden",
            }}>
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Test Chat Client</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {(() => {
                    const onChat = channelAgents.filter(a => (a.channels || []).includes("chat"));
                    const liveOnChat = onChat.filter(a => a.status === "live");
                    return onChat.length > 0 && (
                      <select
                        value={selectedAgent}
                        onChange={e => setSelectedAgent(e.target.value)}
                        style={{
                          padding: "5px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`,
                          background: COLORS.surface, color: COLORS.text, fontSize: 11, outline: "none",
                          cursor: "pointer",
                        }}
                      >
                        <option value="">Auto-route ({liveOnChat.length} live)</option>
                        {onChat.map(a => (
                          <option key={a.id} value={a.id}>{a.name} ({a.status})</option>
                        ))}
                      </select>
                    );
                  })()}
                  <button onClick={handleChatClear} style={{
                    padding: "5px 12px", borderRadius: 6, border: `1px solid ${COLORS.border}`,
                    background: COLORS.surface, color: COLORS.textMuted, fontSize: 11,
                    fontWeight: 600, cursor: "pointer",
                  }}>Clear</button>
                </div>
              </div>

              {/* Chat message area */}
              <div style={{ height: 380, display: "flex", flexDirection: "column" }}>
              <div ref={chatScrollRef} onScroll={() => {
                const el = chatScrollRef.current;
                if (!el) return;
                userScrolledUp.current = el.scrollTop + el.clientHeight < el.scrollHeight - 40;
              }} style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                {wsMessages.length === 0 && !wsAgentTyping && (
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: COLORS.textDim, gap: 8 }}>
                    <div style={{ fontSize: 32 }}>{"\u{1F4AC}"}</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Send a message to test your agents</div>
                    <div style={{ fontSize: 11, color: COLORS.textDim }}>
                      {wsConnected ? "Connected — real-time streaming enabled" : "Connecting..."}
                    </div>
                  </div>
                )}
                {wsMessages.map((msg, i) => (
                  <div key={i}
                    data-testid="chat-message"
                    data-role={msg.role}
                    data-error={msg.error ? "1" : undefined}
                    style={{
                    display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "100%",
                  }}>
                    <div style={{
                      maxWidth: "80%", padding: "10px 14px", borderRadius: 12,
                      background: msg.role === "user" ? COLORS.accent
                        : msg.error ? `${COLORS.pink}18`
                        : msg.role === "system" ? `${COLORS.orange}15`
                        : COLORS.bg,
                      border: msg.error ? `1px solid ${COLORS.pink}30`
                        : msg.role === "assistant" ? `1px solid ${COLORS.border}` : "none",
                      color: msg.role === "user" ? "#fff"
                        : msg.error ? COLORS.pink
                        : msg.role === "system" ? COLORS.orange
                        : COLORS.text,
                    }}>
                      {msg.role === "assistant" && msg.agentName && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.accentText, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                          {msg.agentName}
                          {msg.latencyMs && <span style={{ color: COLORS.textDim, fontWeight: 500 }}>{msg.latencyMs}ms</span>}
                        </div>
                      )}
                      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                        {/* Markdown renders progressively as tokens stream. */}
                        {msg.role === "assistant"
                          ? <MarkdownText text={msg.content || ""} />
                          : <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{msg.content}</span>}
                        {msg.streaming && <span style={{ display: "inline-block", width: 2, height: "1em", background: COLORS.accent, marginLeft: 1, verticalAlign: "text-bottom", animation: "blink 1s step-end infinite" }} />}
                      </div>
                      {msg.fileIds && msg.fileIds.length > 0 && (
                        <div style={{ marginTop: 6, fontSize: 11, opacity: 0.8 }}>
                          {"📎"} {msg.fileIds.length} file{msg.fileIds.length > 1 ? "s" : ""} attached
                        </div>
                      )}
                      {msg.role === "assistant" && !msg.streaming && (
                        <VerificationBadge verification={msg.verification} />
                      )}
                      {msg.role === "assistant" && msg.citations && msg.citations.length > 0 && (
                        <ChatCitations citations={msg.citations} agentId={selectedAgent || undefined} />
                      )}
                      {msg.role === "assistant" && msg.richElements && (
                        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                          {msg.richElements.map((el, j) => {
                            if (el.type === "button") {
                              return (
                                <button key={j} onClick={() => handleRichAction(el.action, el.url)} style={{
                                  padding: "7px 14px", borderRadius: 8, border: `1px solid ${COLORS.accent}`,
                                  background: el.style === "primary" ? COLORS.accent : "transparent",
                                  color: el.style === "primary" ? "#fff" : COLORS.accent,
                                  fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                                }}>{el.label}</button>
                              );
                            }
                            if (el.type === "quick_reply") {
                              return (
                                <button key={j} onClick={() => handleRichAction(el.action)} style={{
                                  display: "inline-block", padding: "5px 12px", borderRadius: 16,
                                  border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                                  color: COLORS.text, fontSize: 11, cursor: "pointer", transition: "all 0.15s",
                                }}>{el.label}</button>
                              );
                            }
                            if (el.type === "card") {
                              return (
                                <div key={j} style={{
                                  border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: "hidden",
                                  background: COLORS.surface,
                                }}>
                                  {el.image && <img src={el.image} alt={el.title || ""} style={{ width: "100%", height: 120, objectFit: "cover" }} />}
                                  <div style={{ padding: "10px 12px" }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{el.title}</div>
                                    {el.subtitle && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{el.subtitle}</div>}
                                    {el.buttons && (
                                      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                                        {el.buttons.map((btn, k) => (
                                          <button key={k} onClick={() => handleRichAction(btn.action, btn.url)} style={{
                                            padding: "5px 12px", borderRadius: 6, border: `1px solid ${COLORS.accent}`,
                                            background: btn.style === "primary" ? COLORS.accent : "transparent",
                                            color: btn.style === "primary" ? "#fff" : COLORS.accent,
                                            fontSize: 11, fontWeight: 600, cursor: "pointer",
                                          }}>{btn.label}</button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                            if (el.type === "carousel") {
                              return (
                                <div key={j} style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                                  {(el.cards || []).map((card, k) => (
                                    <div key={k} style={{
                                      minWidth: 180, border: `1px solid ${COLORS.border}`, borderRadius: 10,
                                      overflow: "hidden", background: COLORS.surface, flexShrink: 0,
                                    }}>
                                      {card.image && <img src={card.image} alt={card.title || ""} style={{ width: "100%", height: 90, objectFit: "cover" }} />}
                                      <div style={{ padding: "8px 10px" }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{card.title}</div>
                                        {card.subtitle && <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>{card.subtitle}</div>}
                                        {card.buttons && (
                                          <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                                            {card.buttons.map((btn, l) => (
                                              <button key={l} onClick={() => handleRichAction(btn.action, btn.url)} style={{
                                                padding: "4px 10px", borderRadius: 6, border: `1px solid ${COLORS.accent}`,
                                                background: "transparent", color: COLORS.accentText, fontSize: 10,
                                                fontWeight: 600, cursor: "pointer",
                                              }}>{btn.label}</button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            if (el.type === "image") {
                              return (
                                <div key={j}>
                                  <img src={el.url} alt={el.alt || ""} style={{ maxWidth: "100%", borderRadius: 8 }} />
                                  {el.caption && <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 4 }}>{el.caption}</div>}
                                </div>
                              );
                            }
                            // Skills v2 CSAT-INFER: explicit star CSAT dropped —
                            // satisfaction is inferred from the transcript at close.
                            if (el.type === "chart") {
                              const data = el.data || {};
                              const labels = data.labels || [];
                              const values = data.values || [];
                              const maxVal = Math.max(...values, 1);
                              const chartColors = ["#4F46E5", "#06B6D4", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6"];
                              return (
                                <div key={j} style={{ padding: "12px 14px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
                                  {el.title && <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>{el.title}</div>}
                                  {(el.chart_type === "bar" || !el.chart_type) && (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                      {labels.map((label, k) => (
                                        <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                          <div style={{ width: 70, fontSize: 10, color: COLORS.textMuted, textAlign: "right", flexShrink: 0 }}>{label}</div>
                                          <div style={{ flex: 1, background: COLORS.surface, borderRadius: 4, height: 20, overflow: "hidden" }}>
                                            <div style={{ width: `${(values[k] / maxVal) * 100}%`, height: "100%", background: chartColors[k % chartColors.length], borderRadius: 4, transition: "width 0.5s" }} />
                                          </div>
                                          <div style={{ width: 40, fontSize: 10, color: COLORS.text, fontWeight: 600, textAlign: "right" }}>{values[k]}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {el.chart_type === "pie" && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                      <svg width="80" height="80" viewBox="0 0 32 32">
                                        {(() => {
                                          const total = values.reduce((a, b) => a + b, 0) || 1;
                                          let cum = 0;
                                          return values.map((v, k) => {
                                            const pct = v / total;
                                            const start = cum;
                                            cum += pct;
                                            const x1 = 16 + 16 * Math.cos(2 * Math.PI * start - Math.PI / 2);
                                            const y1 = 16 + 16 * Math.sin(2 * Math.PI * start - Math.PI / 2);
                                            const x2 = 16 + 16 * Math.cos(2 * Math.PI * cum - Math.PI / 2);
                                            const y2 = 16 + 16 * Math.sin(2 * Math.PI * cum - Math.PI / 2);
                                            const large = pct > 0.5 ? 1 : 0;
                                            return <path key={k} d={`M16,16 L${x1},${y1} A16,16 0 ${large},1 ${x2},${y2} Z`} fill={chartColors[k % chartColors.length]} />;
                                          });
                                        })()}
                                      </svg>
                                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                        {labels.map((label, k) => (
                                          <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10 }}>
                                            <div style={{ width: 8, height: 8, borderRadius: 2, background: chartColors[k % chartColors.length], flexShrink: 0 }} />
                                            <span style={{ color: COLORS.textMuted }}>{label}</span>
                                            <span style={{ color: COLORS.text, fontWeight: 600 }}>{values[k]}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {el.chart_type === "line" && (
                                    <svg width="100%" height="60" viewBox={`0 0 ${labels.length * 40} 60`} style={{ overflow: "visible" }}>
                                      {values.map((v, k) => {
                                        if (k === 0) return null;
                                        const x1 = (k - 1) * 40 + 20;
                                        const x2 = k * 40 + 20;
                                        const y1 = 55 - (values[k - 1] / maxVal) * 50;
                                        const y2 = 55 - (v / maxVal) * 50;
                                        return <line key={k} x1={x1} y1={y1} x2={x2} y2={y2} stroke={COLORS.accent} strokeWidth="2" />;
                                      })}
                                      {values.map((v, k) => (
                                        <g key={k}>
                                          <circle cx={k * 40 + 20} cy={55 - (v / maxVal) * 50} r="3" fill={COLORS.accent} />
                                          <text x={k * 40 + 20} y="58" textAnchor="middle" fontSize="8" fill={COLORS.textDim}>{labels[k]}</text>
                                        </g>
                                      ))}
                                    </svg>
                                  )}
                                </div>
                              );
                            }
                            if (el.type === "table") {
                              return <RichTable key={j} el={el} colors={COLORS} />;
                            }
                            if (el.type === "artifacts") {
                              return <ArtifactChips key={j} artifacts={el.artifacts} />;
                            }
                            if (el.type === "metric") {
                              return (
                                <div key={j} style={{
                                  display: "inline-flex", flexDirection: "column", alignItems: "center",
                                  padding: "12px 20px", borderRadius: 10, background: COLORS.surface,
                                  border: `1px solid ${COLORS.border}`, minWidth: 100,
                                }}>
                                  <div style={{ fontSize: 24, fontWeight: 700, color: el.color || COLORS.accent }}>{el.value}</div>
                                  <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>{el.label}</div>
                                  {el.change && <div style={{ fontSize: 10, color: el.change > 0 ? COLORS.green : (COLORS.red || "#ef4444"), marginTop: 2 }}>{el.change > 0 ? "+" : ""}{el.change}%</div>}
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      )}
                      {msg.role === "assistant" && msg.intent && (
                        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Intent: {msg.intent}</div>
                      )}
                      {msg.role === "user" && (
                        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4, marginTop: 3 }}>
                          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.5)" }}>
                            {(msg.ts || msg.timestamp) ? new Date(msg.ts || msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                          </span>
                          {(!msg.status || msg.status === "sent") && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{"\u2713"}</span>}
                          {msg.status === "delivered" && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>{"\u2713\u2713"}</span>}
                          {msg.status === "read" && <span style={{ fontSize: 11, color: "#60a5fa" }}>{"\u2713\u2713"}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {wsAgentTyping && (
                  <div style={{ display: "flex", gap: 4, padding: "8px 0" }}>
                    {[0, 1, 2].map(i => (
                      <div key={i} style={{
                        width: 6, height: 6, borderRadius: "50%", background: COLORS.textDim,
                        animation: `fadeIn 0.6s ${i * 0.2}s infinite alternate`,
                      }} />
                    ))}
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Pending files */}
              {pendingFiles.length > 0 && (
                <div style={{
                  padding: "6px 16px", borderTop: `1px solid ${COLORS.border}`,
                  display: "flex", gap: 6, flexWrap: "wrap",
                }}>
                  {pendingFiles.map(f => (
                    <div key={f.id} style={{
                      display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
                      background: f.status === 'error' ? 'rgba(239,68,68,0.1)'
                        : f.status === 'complete' ? 'rgba(16,185,129,0.08)'
                        : `${COLORS.accent}15`,
                      border: `1px solid ${
                        f.status === 'error' ? 'rgba(239,68,68,0.3)'
                        : f.status === 'complete' ? 'rgba(16,185,129,0.3)'
                        : `${COLORS.accent}30`
                      }`,
                      borderRadius: 8, fontSize: 11, position: "relative", overflow: "hidden",
                      minWidth: 120, maxWidth: 240, transition: "all 0.2s",
                    }}>
                      {/* Progress bar background */}
                      {f.status === 'uploading' && (
                        <div style={{
                          position: "absolute", left: 0, top: 0, bottom: 0,
                          width: `${f.progress}%`, background: `${COLORS.accent}20`,
                          transition: "width 0.2s ease", pointerEvents: "none",
                        }} />
                      )}
                      {/* Status icon */}
                      <span style={{ position: "relative", fontSize: 12, flexShrink: 0 }}>
                        {f.status === 'uploading' ? '\u2B06\uFE0F' :
                         f.status === 'processing' ? '\u23F3' :
                         f.status === 'complete' ? '\u2705' : '\u274C'}
                      </span>
                      {/* File info */}
                      <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
                        <div style={{
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          color: f.status === 'error' ? '#EF4444' : COLORS.text, fontWeight: 500,
                        }}>{f.filename}</div>
                        <div style={{ fontSize: 10, color: f.status === 'error' ? '#F87171' : COLORS.textMuted, marginTop: 1 }}>
                          {f.status === 'uploading' ? `Uploading ${f.progress}%` :
                           f.status === 'processing' ? 'Processing...' :
                           f.status === 'complete' ? (f.parseStatus || 'Ready') :
                           (f.error || 'Failed')}
                        </div>
                      </div>
                      {/* Remove button */}
                      <button onClick={() => removePendingFile(f.id)} style={{
                        background: "none", border: "none", position: "relative",
                        color: f.status === 'error' ? '#EF4444' : COLORS.textDim,
                        cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1,
                      }}>{"\u00D7"}</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Input area */}
              <div style={{
                padding: "12px 16px", borderTop: pendingFiles.length > 0 ? "none" : `1px solid ${COLORS.border}`,
                display: "flex", gap: 8, alignItems: "center",
              }}>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} multiple
                  accept=".pdf,.docx,.doc,.txt,.md,.csv,.html,.json,.xml,.yaml,.yml,.log,.ini,.cfg,.toml,.py,.js,.ts,.jsx,.tsx,.java,.c,.cpp,.go,.rs,.rb,.sh,.png,.jpg,.jpeg,.gif,.webp"
                  style={{ display: "none" }}
                />
                <button onClick={() => fileInputRef.current?.click()} title="Attach files" style={{
                  padding: "8px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                  background: COLORS.surface, color: COLORS.textDim, cursor: "pointer",
                  fontSize: 16, lineHeight: 1, display: "flex", alignItems: "center",
                }}>
                  {"\u{1F4CE}"}
                </button>
                <input
                  type="text"
                  data-testid="chat-input"
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleChatSend()}
                  placeholder={pendingFiles.length > 0 ? "Add a message about your files..." : "Type a message..."}
                  disabled={wsStreaming || !wsConnected}
                  style={{
                    flex: 1, padding: "10px 14px", borderRadius: 10,
                    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                    color: COLORS.text, fontSize: 13, outline: "none",
                    transition: "border-color 0.15s",
                  }}
                  onFocus={e => e.target.style.borderColor = COLORS.accent}
                  onBlur={e => e.target.style.borderColor = COLORS.border}
                />
                <button onClick={handleChatSend} data-testid="chat-send" disabled={wsStreaming || fileUploading || !wsConnected || (!chatInput.trim() && !pendingFiles.some(f => f.status === 'complete'))} style={{
                  padding: "10px 20px", borderRadius: 10, border: "none", cursor: wsStreaming ? "wait" : "pointer",
                  background: (!chatInput.trim() && !pendingFiles.some(f => f.status === 'complete')) ? COLORS.border : `linear-gradient(135deg, ${COLORS.accent}, #6366F1)`,
                  color: (!chatInput.trim() && !pendingFiles.some(f => f.status === 'complete')) ? COLORS.textDim : "#fff",
                  fontSize: 13, fontWeight: 600,
                  boxShadow: (chatInput.trim() || pendingFiles.some(f => f.status === 'complete')) ? `0 4px 12px ${COLORS.accentGlow}` : "none",
                  transition: "all 0.15s",
                }}>Send</button>
              </div>
            </div>
            </div>
          </div>
        )}

        {/* -- Voice -- */}
        {activeChannel === "voice" && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: bp.isMobile ? 18 : 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>Voice Channel</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Real-time voice via LiveKit \u00B7 Deepgram STT \u00B7 OpenAI LLM \u00B7 ElevenLabs TTS</div>
            </div>

            {/* Status card */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px", marginBottom: 14,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: "50%",
                    background: voiceConfig?.deepgram_configured && voiceConfig?.elevenlabs_configured ? COLORS.green : COLORS.orange,
                    boxShadow: `0 0 6px ${voiceConfig?.deepgram_configured && voiceConfig?.elevenlabs_configured ? COLORS.green : COLORS.orange}66`,
                  }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
                    {voiceConfig?.deepgram_configured && voiceConfig?.elevenlabs_configured ? "Voice Ready" : "Voice Not Configured"}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>LiveKit WebRTC</div>
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                  Deepgram: <span style={{ color: voiceConfig?.deepgram_configured ? COLORS.green : COLORS.pink, fontWeight: 600 }}>
                    {voiceConfig?.deepgram_configured ? "Connected" : "Not set"}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                  ElevenLabs: <span style={{ color: voiceConfig?.elevenlabs_configured ? COLORS.green : COLORS.pink, fontWeight: 600 }}>
                    {voiceConfig?.elevenlabs_configured ? "Connected" : "Not set"}
                  </span>
                </div>
              </div>
            </div>

            <AgentsOnChannel agents={channelAgents} channel="voice" label="Voice" mobile={bp.isMobile} />

            {/* Voice Configuration */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px", marginBottom: 14,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Voice Configuration</div>
                {!voiceEditing ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={handleVoiceTest} disabled={voiceTesting || !(voiceConfig?.deepgram_configured && voiceConfig?.elevenlabs_configured)} style={{
                      padding: "6px 14px", borderRadius: 8, border: "none", cursor: voiceTesting ? "wait" : "pointer",
                      background: !(voiceConfig?.deepgram_configured && voiceConfig?.elevenlabs_configured) ? COLORS.border : `linear-gradient(135deg, ${COLORS.green}, #059669)`,
                      color: !(voiceConfig?.deepgram_configured && voiceConfig?.elevenlabs_configured) ? COLORS.textDim : "#fff",
                      fontSize: 12, fontWeight: 600,
                      boxShadow: !(voiceConfig?.deepgram_configured && voiceConfig?.elevenlabs_configured) ? "none" : "0 4px 12px rgba(16,185,129,0.3)",
                    }}>{voiceTesting ? "Testing..." : "Test Connection"}</button>
                    <button onClick={() => setVoiceEditing(true)} style={{
                      padding: "6px 14px", borderRadius: 8, border: `1px solid ${COLORS.accent}40`,
                      background: `${COLORS.accent}10`, color: COLORS.accentText, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}>Edit</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => { setVoiceEditing(false); }} style={{
                      padding: "6px 14px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                      background: COLORS.surface, color: COLORS.textMuted, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}>Cancel</button>
                    <button onClick={handleVoiceSave} disabled={voiceSaving} style={{
                      padding: "6px 14px", borderRadius: 8, border: "none",
                      background: voiceSaved ? COLORS.green : `linear-gradient(135deg, ${COLORS.green}, #059669)`, color: "#fff",
                      fontSize: 12, fontWeight: 600, cursor: voiceSaving ? "wait" : "pointer",
                      boxShadow: "0 4px 12px rgba(16,185,129,0.3)",
                    }}>{voiceSaving ? "Saving..." : voiceSaved ? "Saved" : "Save"}</button>
                  </div>
                )}
              </div>

              {voiceSaved && (
                <div style={{
                  padding: "8px 12px", borderRadius: 8, marginBottom: 12,
                  background: `${COLORS.green}10`, border: `1px solid ${COLORS.green}25`,
                }}>
                  <span style={{ fontSize: 12, color: COLORS.green, fontWeight: 600 }}>Configuration saved</span>
                </div>
              )}

              {/* Deepgram API Key */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted }}>Deepgram API Key</label>
                  {voiceConfig?.deepgram_configured && <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.green }} />}
                </div>
                <input
                  type="password" value={voiceDeepgramKey}
                  onChange={e => setVoiceDeepgramKey(e.target.value)}
                  disabled={!voiceEditing}
                  placeholder={voiceConfig?.deepgram_configured ? voiceConfig.deepgram_api_key : "Enter Deepgram API key"}
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 8,
                    border: `1px solid ${COLORS.border}`, background: voiceEditing ? COLORS.bg : `${COLORS.bg}80`,
                    color: COLORS.text, fontSize: 12, outline: "none",
                    opacity: voiceEditing ? 1 : 0.7,
                  }}
                />
              </div>

              {/* ElevenLabs API Key */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted }}>ElevenLabs API Key</label>
                  {voiceConfig?.elevenlabs_configured && <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.green }} />}
                </div>
                <input
                  type="password" value={voiceElevenlabsKey}
                  onChange={e => setVoiceElevenlabsKey(e.target.value)}
                  disabled={!voiceEditing}
                  placeholder={voiceConfig?.elevenlabs_configured ? voiceConfig.elevenlabs_api_key : "Enter ElevenLabs API key"}
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 8,
                    border: `1px solid ${COLORS.border}`, background: voiceEditing ? COLORS.bg : `${COLORS.bg}80`,
                    color: COLORS.text, fontSize: 12, outline: "none",
                    opacity: voiceEditing ? 1 : 0.7,
                  }}
                />
              </div>

              {/* ElevenLabs Voice ID */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 6 }}>ElevenLabs Voice ID</label>
                <input
                  type="text" value={voiceVoiceId}
                  onChange={e => setVoiceVoiceId(e.target.value)}
                  disabled={!voiceEditing}
                  placeholder="21m00Tcm4TlvDq8ikWAM"
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 8,
                    border: `1px solid ${COLORS.border}`, background: voiceEditing ? COLORS.bg : `${COLORS.bg}80`,
                    color: COLORS.text, fontSize: 12, outline: "none",
                    opacity: voiceEditing ? 1 : 0.7,
                  }}
                />
                <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Default: Rachel (21m00Tcm4TlvDq8ikWAM)</div>
              </div>

              {/* ElevenLabs Model */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 6 }}>ElevenLabs Model</label>
                <select
                  value={voiceModel}
                  onChange={e => setVoiceModel(e.target.value)}
                  disabled={!voiceEditing}
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 8,
                    border: `1px solid ${COLORS.border}`, background: voiceEditing ? COLORS.bg : `${COLORS.bg}80`,
                    color: COLORS.text, fontSize: 12, outline: "none", cursor: voiceEditing ? "pointer" : "default",
                    opacity: voiceEditing ? 1 : 0.7,
                  }}
                >
                  <option value="eleven_v3">Eleven v3 (Latest)</option>
                  <option value="eleven_turbo_v2_5">Eleven Turbo v2.5 (Fast)</option>
                  <option value="eleven_flash_v2_5">Eleven Flash v2.5 (Fastest)</option>
                  <option value="eleven_multilingual_v2">Eleven Multilingual v2</option>
                  <option value="eleven_turbo_v2">Eleven Turbo v2</option>
                  <option value="eleven_flash_v2">Eleven Flash v2</option>
                  <option value="eleven_multilingual_v1">Eleven Multilingual v1</option>
                  <option value="eleven_monolingual_v1">Eleven English v1</option>
                </select>
                <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Flash/Turbo models have lower latency; v3 and Multilingual v2 offer best quality</div>
              </div>

              {/* ElevenLabs Voice Tuning */}
              <div style={{ marginBottom: 16, padding: 14, borderRadius: 10, background: `${COLORS.card}80`, border: `1px solid ${COLORS.border}` }}>
                <Label style={{ marginBottom: 12 }}>Voice Tuning</Label>

                {[
                  { label: "Stability", value: voiceStability, set: setVoiceStability, min: 0, max: 1, step: 0.01, desc: "Lower = more expressive, Higher = more consistent" },
                  { label: "Similarity Boost", value: voiceSimilarity, set: setVoiceSimilarity, min: 0, max: 1, step: 0.01, desc: "How closely to match the original voice" },
                  { label: "Style Exaggeration", value: voiceStyle, set: setVoiceStyle, min: 0, max: 1, step: 0.01, desc: "Amplify the voice's speaking style" },
                  { label: "Speed", value: voiceSpeed, set: setVoiceSpeed, min: 0.5, max: 2.0, step: 0.05, desc: "Playback speed (1.0 = normal)" },
                ].map(s => (
                  <div key={s.label} style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>{s.label}</label>
                      <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.accentText, fontFamily: "monospace" }}>{s.value.toFixed(2)}</span>
                    </div>
                    <input
                      type="range" min={s.min} max={s.max} step={s.step} value={s.value}
                      onChange={e => s.set(parseFloat(e.target.value))}
                      disabled={!voiceEditing}
                      style={{ width: "100%", accentColor: COLORS.accent, height: 4, cursor: voiceEditing ? "pointer" : "default", opacity: voiceEditing ? 1 : 0.7 }}
                    />
                    <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2 }}>{s.desc}</div>
                  </div>
                ))}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>Speaker Boost</label>
                  <button
                    onClick={() => voiceEditing && setVoiceSpeakerBoost(!voiceSpeakerBoost)}
                    disabled={!voiceEditing}
                    style={{
                      padding: "3px 10px", borderRadius: 6, border: `1px solid ${voiceSpeakerBoost ? COLORS.accent : COLORS.border}`,
                      background: voiceSpeakerBoost ? `${COLORS.accent}20` : "transparent",
                      color: voiceSpeakerBoost ? COLORS.accent : COLORS.textDim,
                      fontSize: 11, fontWeight: 600, cursor: voiceEditing ? "pointer" : "default",
                      opacity: voiceEditing ? 1 : 0.7,
                    }}
                  >{voiceSpeakerBoost ? "ON" : "OFF"}</button>
                </div>
                <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2 }}>Enhance voice clarity and presence</div>
              </div>

              {/* Test result */}
              {voiceTestResult && (
                <div style={{
                  marginTop: 10, padding: "10px 14px", borderRadius: 8,
                  background: voiceTestResult.all_ok ? `${COLORS.green}12` : `${COLORS.pink}12`,
                  border: `1px solid ${voiceTestResult.all_ok ? COLORS.green : COLORS.pink}25`,
                }}>
                  {voiceTestResult.all_ok ? (
                    <div style={{ fontSize: 12, color: COLORS.green, fontWeight: 600 }}>All services connected successfully</div>
                  ) : (
                    <div>
                      {voiceTestResult.data?.deepgram && (
                        <div style={{ fontSize: 11, color: voiceTestResult.data.deepgram.ok ? COLORS.green : COLORS.pink, marginBottom: 4 }}>
                          Deepgram: {voiceTestResult.data.deepgram.ok ? "OK" : voiceTestResult.data.deepgram.error || `HTTP ${voiceTestResult.data.deepgram.status}`}
                        </div>
                      )}
                      {voiceTestResult.data?.elevenlabs && (
                        <div style={{ fontSize: 11, color: voiceTestResult.data.elevenlabs.ok ? COLORS.green : COLORS.pink }}>
                          ElevenLabs: {voiceTestResult.data.elevenlabs.ok ? "OK" : voiceTestResult.data.elevenlabs.error || `HTTP ${voiceTestResult.data.elevenlabs.status}`}
                        </div>
                      )}
                      {voiceTestResult.error && (
                        <div style={{ fontSize: 11, color: COLORS.pink }}>{voiceTestResult.error}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Voice Webhook URLs */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px", marginBottom: 14,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Webhook URLs</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>Configure these URLs in your Twilio console for inbound voice calls</div>
              {[
                { label: "Voice Webhook", path: `/webhooks/${waConfig?.workspace || "workspace"}/twilio/voice` },
                { label: "Gather (DTMF/Speech)", path: `/webhooks/${waConfig?.workspace || "workspace"}/twilio/voice/gather` },
                { label: "Status Callback", path: `/webhooks/${waConfig?.workspace || "workspace"}/twilio/status` },
              ].map(wh => (
                <div key={wh.path} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 4, fontWeight: 500 }}>{wh.label}</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input type="text" readOnly value={`${waConfig?.webhook_base_url || "https://your-domain.com"}${wh.path}`} style={{
                      flex: 1, padding: "8px 12px", borderRadius: 8,
                      border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                      color: COLORS.text, fontSize: 11, outline: "none", fontFamily: "monospace",
                    }} />
                    <button onClick={() => copyToClipboard(`${waConfig?.webhook_base_url || "https://your-domain.com"}${wh.path}`, wh.path)} style={{
                      padding: "8px 14px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                      background: copied === wh.path ? `${COLORS.green}15` : COLORS.bg,
                      color: copied === wh.path ? COLORS.green : COLORS.textMuted,
                      fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                    }}>{copied === wh.path ? "Copied!" : "Copy"}</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Voice Test Client */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "20px 24px",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Test Voice Client</div>
                <Badge color={voiceStatus === "ready" ? COLORS.green : voiceStatus === "connected" || voiceStatus === "connecting" ? COLORS.orange : COLORS.textDim}>{voiceStatus}</Badge>
              </div>

              {/* Agent selector */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 6 }}>Agent</label>
                <select
                  value={voiceSelectedAgent}
                  onChange={e => setVoiceSelectedAgent(e.target.value)}
                  disabled={voiceActive}
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 8,
                    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
                    color: COLORS.text, fontSize: 12, outline: "none",
                  }}
                >
                  <option value="">Auto (first available)</option>
                  {voiceAgents.map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.status})</option>
                  ))}
                </select>
              </div>

              {/* Microphone button */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 0" }}>
                <button
                  onClick={handleVoiceToggle}
                  disabled={voiceStatus === "connecting"}
                  style={{
                    width: 80, height: 80, borderRadius: "50%", border: "none", cursor: "pointer",
                    background: voiceActive
                      ? `linear-gradient(135deg, ${COLORS.pink}, ${COLORS.orange})`
                      : `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
                    boxShadow: voiceActive
                      ? `0 0 0 8px ${COLORS.pink}15, 0 0 0 16px ${COLORS.pink}08`
                      : `0 4px 20px ${COLORS.accent}40`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.3s", position: "relative",
                  }}
                >
                  <span style={{ fontSize: 32, color: "#fff" }}>
                    {voiceActive ? "\u25A0" : "\u{1F399}"}
                  </span>
                </button>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 12 }}>
                  {voiceStatus === "connecting" ? "Connecting..." : voiceActive ? "Click to stop" : "Click to start voice"}
                </div>
              </div>

              {/* Transcript */}
              {voiceTranscript.length > 0 && (
                <div style={{
                  borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginTop: 8,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted }}>Transcript</div>
                    <button onClick={() => setVoiceTranscript([])} style={{
                      background: "transparent", border: "none", color: COLORS.textDim,
                      fontSize: 10, cursor: "pointer", fontWeight: 600,
                    }}>Clear</button>
                  </div>
                  <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    {voiceTranscript.map((t) => (
                      <div key={t.id} style={{
                        padding: "6px 10px", borderRadius: 8, marginBottom: 4,
                        background: t.role === "user" ? `${COLORS.accent}08` : `${COLORS.green}08`,
                        borderLeft: `2px solid ${t.role === "user" ? COLORS.accent : COLORS.green}`,
                      }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: t.role === "user" ? COLORS.accent : COLORS.green, marginBottom: 2, textTransform: "uppercase" }}>
                          {t.role === "user" ? "You" : "Agent"}
                        </div>
                        <div style={{ fontSize: 12, color: COLORS.text, opacity: t.final === false ? 0.5 : 1 }}>{t.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* -- SMS Config -- */}
        {activeChannel === "sms" && (
          <div>
            <div style={{ display: "flex", alignItems: bp.isMobile ? "flex-start" : "center", justifyContent: "space-between", marginBottom: 24, flexDirection: bp.isMobile ? "column" : "row", gap: bp.isMobile ? 12 : 0 }}>
              <div>
                <div style={{ fontSize: bp.isMobile ? 18 : 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>SMS Configuration</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Connect your Twilio account to send and receive SMS messages</div>
              </div>
            </div>

            {/* Test result */}
            {smsTestResult && (
              <div style={{
                padding: "12px 16px", borderRadius: 10, marginBottom: 16,
                background: smsTestResult.success ? `${COLORS.green}10` : `${COLORS.pink}10`,
                border: `1px solid ${smsTestResult.success ? COLORS.green : COLORS.pink}25`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: smsTestResult.success ? COLORS.green : COLORS.pink }}>
                  {smsTestResult.success ? "Connection Successful" : "Connection Failed"}
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                  {smsTestResult.success ? `Account: ${smsTestResult.data?.friendly_name || "Connected"}` : smsTestResult.error}
                </div>
              </div>
            )}

            {/* Status card */}
            <div style={{ padding: "16px 20px", borderRadius: 12, border: `1px solid ${COLORS.border}`, marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700,
                background: smsConfig?.configured ? `${COLORS.green}12` : `${COLORS.orange}12`,
                border: `1px solid ${smsConfig?.configured ? COLORS.green : COLORS.orange}25`,
              }}>{smsConfig?.configured ? "\u2713" : "!"}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                  {smsConfig?.configured ? "Twilio Connected" : "Twilio Not Configured"}
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                  {smsConfig?.configured
                    ? `Phone: ${smsConfig.phone_number}`
                    : "Add your Twilio credentials to enable SMS"
                  }
                </div>
              </div>
            </div>

            <AgentsOnChannel agents={channelAgents} channel="sms" label="SMS" mobile={bp.isMobile} />

            {/* Config form */}
            <div style={{ background: COLORS.surface, borderRadius: 12, border: `1px solid ${COLORS.border}`, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>Twilio Credentials</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={async () => {
                    setSmsTestLoading(true); setSmsTestResult(null);
                    try {
                      const res = await authApi.testSMSConfig();
                      setSmsTestResult({ success: true, data: res.data });
                    } catch (err) {
                      setSmsTestResult({ success: false, error: err.message || "Connection failed" });
                    } finally { setSmsTestLoading(false); }
                  }} disabled={smsTestLoading || !smsConfig?.configured} style={{
                    padding: "6px 14px", borderRadius: 8, border: "none", fontSize: 11, fontWeight: 600, cursor: "pointer",
                    background: !smsConfig?.configured ? COLORS.border : `linear-gradient(135deg, ${COLORS.green}, #059669)`,
                    color: !smsConfig?.configured ? COLORS.textDim : "#fff",
                  }}>{smsTestLoading ? "Testing..." : "Test Connection"}</button>
                  {!smsEditing ? (
                    <button onClick={() => setSmsEditing(true)} style={{
                      padding: "6px 14px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                      background: COLORS.surface, color: COLORS.text, fontSize: 11, fontWeight: 600, cursor: "pointer",
                    }}>Edit</button>
                  ) : (
                    <>
                      <button onClick={async () => {
                        setSmsSaving(true); setSmsSaveMsg(null);
                        try {
                          await authApi.updateSMSConfig({
                            twilio_account_sid: smsForm.account_sid,
                            twilio_auth_token: smsForm.auth_token,
                            twilio_phone_number: smsForm.phone_number,
                          });
                          const fresh = await authApi.getSMSConfig();
                          if (fresh.success) { setSmsConfig(fresh.data); setSmsForm({ account_sid: fresh.data.account_sid || "", auth_token: fresh.data.auth_token || "", phone_number: fresh.data.phone_number || "" }); }
                          setSmsEditing(false); setSmsSaveMsg("Saved!");
                          setTimeout(() => setSmsSaveMsg(null), 2000);
                        } catch (err) { setSmsSaveMsg("Failed to save"); }
                        finally { setSmsSaving(false); }
                      }} disabled={smsSaving} style={{
                        padding: "6px 14px", borderRadius: 8, border: "none",
                        background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.pink})`,
                        color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer",
                      }}>{smsSaving ? "Saving..." : "Save"}</button>
                      <button onClick={() => { setSmsEditing(false); if (smsConfig) setSmsForm({ account_sid: smsConfig.account_sid || "", auth_token: smsConfig.auth_token || "", phone_number: smsConfig.phone_number || "" }); }} style={{
                        padding: "6px 14px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                        background: COLORS.surface, color: COLORS.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer",
                      }}>Cancel</button>
                    </>
                  )}
                </div>
              </div>
              {smsSaveMsg && <div style={{ fontSize: 11, color: COLORS.green, marginBottom: 8 }}>{smsSaveMsg}</div>}

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 500, display: "block", marginBottom: 4 }}>Account SID</label>
                  <input type="text" value={smsForm.account_sid} disabled={!smsEditing} onChange={e => setSmsForm(p => ({ ...p, account_sid: e.target.value }))} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 500, display: "block", marginBottom: 4 }}>Auth Token</label>
                  <input type="password" value={smsForm.auth_token} disabled={!smsEditing} onChange={e => setSmsForm(p => ({ ...p, auth_token: e.target.value }))} placeholder="Twilio auth token" style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 500, display: "block", marginBottom: 4 }}>Phone Number</label>
                  <input type="text" value={smsForm.phone_number} disabled={!smsEditing} onChange={e => setSmsForm(p => ({ ...p, phone_number: e.target.value }))} placeholder="+1234567890" style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Webhook URL */}
            <div style={{ marginTop: 20, background: COLORS.surface, borderRadius: 12, border: `1px solid ${COLORS.border}`, padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>SMS Webhook URL</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>Enter this URL in your Twilio SMS webhook configuration</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input type="text" readOnly value={smsConfig?.webhook_url || ""} style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => copyToClipboard(smsConfig?.webhook_url, "sms-webhook")} style={{
                  padding: "8px 14px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                  background: copied === "sms-webhook" ? `${COLORS.green}15` : COLORS.bg,
                  color: copied === "sms-webhook" ? COLORS.green : COLORS.textMuted,
                  fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                }}>{copied === "sms-webhook" ? "Copied!" : "Copy"}</button>
              </div>
            </div>

            {/* Setup guide */}
            <div style={{ marginTop: 20, background: COLORS.surface, borderRadius: 12, border: `1px solid ${COLORS.border}`, padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 12 }}>Setup Guide</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  { step: 1, title: "Create a Twilio account", desc: "Sign up at twilio.com and get your Account SID and Auth Token from the console dashboard." },
                  { step: 2, title: "Get a phone number", desc: "Purchase a Twilio phone number with SMS capability from the Phone Numbers section." },
                  { step: 3, title: "Enter credentials above", desc: "Paste your Account SID, Auth Token, and phone number into the fields above." },
                  { step: 4, title: "Configure the webhook", desc: "In Twilio's Phone Number settings, set the SMS webhook URL to the one shown above (POST method)." },
                  { step: 5, title: "Test the connection", desc: "Click 'Test Connection' above and send a test SMS to verify everything works." },
                ].map(s => (
                  <div key={s.step} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                      background: `${COLORS.pink}15`, border: `1px solid ${COLORS.pink}30`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 700, color: COLORS.pink,
                    }}>{s.step}</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{s.title}</div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SMS Test Client */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              overflow: "hidden", marginTop: 20,
            }}>
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Test SMS Client</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {(() => {
                    const liveAgents = channelAgents.filter(a => a.status === "live" && (a.channels || []).includes("sms"));
                    return liveAgents.length > 0 && (
                      <select value={smsTestAgent} onChange={e => setSmsTestAgent(e.target.value)}
                        style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 11, outline: "none", cursor: "pointer" }}>
                        <option value="">Auto-route ({liveAgents.length} live)</option>
                        {liveAgents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.status})</option>)}
                      </select>
                    );
                  })()}
                  <button onClick={() => { setSmsTestMessages([]); localStorage.removeItem("i2_sms_messages"); }} style={{
                    padding: "5px 12px", borderRadius: 6, border: `1px solid ${COLORS.border}`,
                    background: COLORS.surface, color: COLORS.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer",
                  }}>Clear</button>
                </div>
              </div>
              <div style={{ height: 380, display: "flex", flexDirection: "column" }}>
                <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                  {smsTestMessages.length === 0 && (
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: COLORS.textDim, gap: 8 }}>
                      <div style={{ fontSize: 32 }}>{"\uD83D\uDCE8"}</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>Test your SMS agent</div>
                      <div style={{ fontSize: 11, color: COLORS.textDim }}>Messages are processed through the orchestrator with channel=sms</div>
                    </div>
                  )}
                  {smsTestMessages.map((msg, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", maxWidth: "100%" }}>
                      <div style={{
                        maxWidth: "80%", padding: "10px 14px", borderRadius: 12,
                        background: msg.role === "user" ? COLORS.pink : msg.role === "error" ? `${COLORS.pink}18` : COLORS.bg,
                        border: msg.role === "error" ? `1px solid ${COLORS.pink}30` : msg.role === "assistant" ? `1px solid ${COLORS.border}` : "none",
                        color: msg.role === "user" ? "#fff" : msg.role === "error" ? COLORS.pink : COLORS.text,
                      }}>
                        {msg.role === "assistant" && msg.agentName && (
                          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.pink, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                            {msg.agentName}
                            {msg.latency && <span style={{ color: COLORS.textDim, fontWeight: 500 }}>{msg.latency}ms</span>}
                          </div>
                        )}
                        <MarkdownText text={msg.text} style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }} />
                      </div>
                    </div>
                  ))}
                  {smsTestMsgLoading && (
                    <div style={{ display: "flex", justifyContent: "flex-start" }}>
                      <div style={{ padding: "10px 14px", borderRadius: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`, fontSize: 13, color: COLORS.textDim }}>Typing...</div>
                    </div>
                  )}
                </div>
                <div style={{ padding: "12px 20px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 8 }}>
                  <input type="text" value={smsTestInput} onChange={e => setSmsTestInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSmsTestSend(); } }}
                    placeholder="Type an SMS message..."
                    style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 13, outline: "none" }} />
                  <button onClick={handleSmsTestSend} disabled={smsTestMsgLoading || !smsTestInput.trim()}
                    style={{
                      padding: "10px 20px", borderRadius: 10, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer",
                      background: smsTestMsgLoading || !smsTestInput.trim() ? COLORS.border : `linear-gradient(135deg, ${COLORS.pink}, #DB2777)`,
                      color: smsTestMsgLoading || !smsTestInput.trim() ? COLORS.textDim : "#fff",
                    }}>Send</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeChannel === "email" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Email Channel Configuration */}
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: "24px 28px" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>Email Channel</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 20, lineHeight: 1.6 }}>
                Configure inbound email processing. Customers email your support address, the AI agent replies automatically with full conversation threading.
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Webhook URL</div>
                  <div style={{
                    padding: "10px 14px", borderRadius: 8, fontSize: 12, fontFamily: "monospace",
                    background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text,
                    wordBreak: "break-all",
                  }}>
                    {`https://${window.location.host}/webhooks/${currentTenant?.slug || "<workspace>"}/email`}
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
                    Configure your email provider (SendGrid, AWS SES, or Mailgun) to forward inbound emails to this URL.
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Supported Providers</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {["SendGrid Inbound Parse", "AWS SES (via SNS)", "Mailgun Routes", "Generic JSON Webhook"].map(p => (
                      <span key={p} style={{
                        padding: "4px 10px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                        background: `${COLORS.cyan}12`, border: `1px solid ${COLORS.cyan}25`, color: COLORS.cyan,
                      }}>{p}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Outbound Replies</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
                    AI replies are sent via SMTP (configured in Admin &gt; API Keys &gt; SMTP / Email).
                    Replies include proper email threading headers (In-Reply-To, References) so
                    conversations appear as threads in Gmail, Outlook, etc.
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Features</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {[
                      "AI responds with professional, properly formatted emails",
                      "Email threading \u2014 replies appear as threads in customer's inbox",
                      "Anti-loop protection \u2014 auto-replies and mailer daemons are ignored",
                      "Cross-channel identity \u2014 email handle linked to unified profile (UCID)",
                      "Escalation \u2014 hands off to Genesys or supervisor when needed",
                      "Attachments \u2014 inbound attachments processed by the pipeline",
                    ].map((f, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ color: COLORS.green, fontSize: 12 }}>{"\u2714"}</span>
                        <span style={{ fontSize: 11, color: COLORS.textMuted }}>{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Agents on Email */}
            <AgentsOnChannel agents={channelAgents} channel="email" label="Email" mobile={bp.isMobile} />
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default ChannelsView;
